import { useState, useEffect, useMemo } from "react";
import { ApplicationContext } from "./ApplicationContext";
import { jwtDecode } from "jwt-decode";

const ApplicationContextProvider = ({ children }) => {
  const [loggedInUser, setLoggedInUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token).data;
        setLoggedInUser(decoded);
      } catch (err) {
        console.error("Invalid JWT", err);
      }
    }
  }, []);

  const roles = useMemo(() => {
    if (!loggedInUser) return {};

    const userRole = Number(loggedInUser?.role);
    return {
      isAdminHrRole: userRole === 1 || userRole === 2,
      isManagerRole: userRole === 4 || userRole === 5 || userRole === 8,
      isEmployeeRole: userRole === 7,
      // HR Manager specifically (not Admin) — Grievance's Investigate action
      // is scoped to this role only, unlike Approve/Reject.
      isHrManagerRole: userRole === 2,
      // role_id 4 also falls under isManagerRole above (kept as-is for
      // whatever else already relies on that general bucket) - this is
      // the specific check for Finance Manager-only actions like
      // periodic payroll Approve/Reject/Hold.
      isFinanceManagerRole: userRole === 4,
      isAdminRole: userRole === 1,
      // Admin always gets what the HR Manager gets — Investigate, status
      // updates, internal notes. Mirrors HR_MANAGER_ROLES on the backend.
      canManageGrievance: userRole === 1 || userRole === 2,
      // Anyone on the HR side of a case (adds HR Executive) — for the
      // "my assigned" filter.
      canHandleGrievance: [1, 2, 3].includes(userRole),
      // Grievance Management is only for Admin, HR Manager, HR Executive
      // (assigned grievances), Manager and Employee — mirrors
      // GRIEVANCE_ALLOWED_ROLES on the backend.
      canAccessGrievance: [1, 2, 3, 4, 5, 7, 8].includes(userRole),
    };
  }, [loggedInUser]);

  const updateLoggedInUser = (user) => {
    setLoggedInUser(user || null);
  };

  const ctxValue = {
    loggedInUser,
    setLoggedInUser: updateLoggedInUser,
    ...roles,
  };

  return (
    <ApplicationContext.Provider value={ctxValue}>
      {children}
    </ApplicationContext.Provider>
  );
};

export default ApplicationContextProvider;
